"use client";

import { useEffect } from "react";
import { ImageIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ImageViewer } from "@/components/image-viewer";
import { ImageViewerToolbar } from "@/components/image-viewer-toolbar";
import { Button } from "@/components/ui/button";

export interface ImageViewerPageProps {
  url: string;
  fileName: string;
  onClose: () => void;
}

/** Standalone full-viewport signed-image viewer used by chat pop-out URLs. */
export function ImageViewerPage({ url, fileName, onClose }: ImageViewerPageProps) {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = fileName;
    return () => {
      document.title = import.meta.env.APP_NAME ?? "Layer Nine";
    };
  }, [fileName]);

  return (
    <div className="flex h-screen min-h-0 flex-col bg-background text-foreground">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <ImageIcon className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-semibold" title={fileName}>
            {fileName}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <ImageViewerToolbar fileUrl={url} fileName={fileName} />
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={onClose}
            aria-label={t("viewer.closeViewer")}
          >
            <X className="size-4" />
          </Button>
        </div>
      </header>
      <ImageViewer url={url} fileName={fileName} className="min-h-0 flex-1" />
    </div>
  );
}
